import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import debounce from 'utils/debounce'
import usePagination from 'hooks/usePagination'
import { useNotification } from 'components/Notification'
import { getEntityMergeRecords } from 'services/merge'
import { ENTITY_MERGE_RECORDS_QUERY_KEY } from '../constants'
import { formatErrorMessage } from '../utils'

const defaultFilters = {
  term: '',
  status: '',
}

const debouncedFetch = debounce(getEntityMergeRecords, 300)

function useEntityMergeRecords(mergeType) {
  const { pagination, handlePageChange, handleRowsChange } = usePagination()
  const [filters, setFilters] = useState(defaultFilters)
  const { createNotification } = useNotification()

  const { data, isLoading, isError, error, refetch } = useQuery(
    [ENTITY_MERGE_RECORDS_QUERY_KEY, mergeType, pagination, filters],
    () =>
      debouncedFetch({
        mergeType,
        pageSize: pagination.pageSize,
        pageOffset: pagination.pageOffset,
        ...(filters.term && { term: filters.term }),
        ...(filters.status && { status: filters.status }),
      }),
    {
      placeholderData: { data: [], meta: { itemCount: 0 } },
    },
  )

  useEffect(() => {
    if (isError) {
      const message = formatErrorMessage(error, 'Failed to load merge records. Please try again.')
      createNotification({ message, variant: 'error' })
    }
  }, [isError, error, createNotification])

  const handleTermChange = event => {
    const term = event.target.value || ''
    setFilters(prev => ({ ...prev, term }))
    handlePageChange(0)
  }

  const handleStatusChange = e => {
    setFilters(prev => ({ ...prev, status: e.target.value }))
    handlePageChange(0)
  }

  return {
    records: data.data,
    pagination,
    itemCount: data.meta.itemCount,
    filters,
    isLoading,
    refetch,
    handlePageChange,
    handlePageSizeChange: handleRowsChange,
    handleTermChange,
    handleStatusChange,
  }
}

export default useEntityMergeRecords
